import { BaseModel } from "~/classes/models/base.model";
import { SheetController } from "~/components/Base/Sheet/controller";
import { Assignments, Assignment } from "../assignments/assignment.model";
import { Classrooms, Classroom } from "../classrooms/classroom.model";
import { Students, Student } from "../students/student.model";
const { createDocument, updateDocumentStateById } = useDocuments();
import type { Ref } from "vue";

type SubmissionJobConstructorParams = {
  id: string;
  sheetController: SheetController;
  checklists?: Checklists;
  checklist?: Checklist;

  classrooms?: Classrooms;
  classroom?: Classroom;
  students?: Students;
  student?: Student;
  assignments?: Assignments;
  assignment?: Assignment;

  uploadMethod: "google" | "computer";

  documentSubmissionType: DocumentSubmissionType;
  submittedDocumentState: DocumentState;
  attachChecklist: boolean;
  accessToken: string;

  files: any[];
};

export type SubmissionJobs = SubmissionJob[];

export class SubmissionJob extends BaseModel {
  id: string;
  sheetController: SheetController;
  checklists?: Checklists;
  checklist?: Checklist;
  classroom?: Classroom;
  classrooms?: Classrooms;
  students?: Students;
  student?: Student;
  assignments?: Assignments;
  assignment?: Assignment;
  uploadMethod: "google" | "computer";
  accessToken: string;

  documentSubmissionType: DocumentSubmissionType;
  submittedDocumentState: DocumentState;
  attachChecklist: boolean;
  filesBeingUploaded: any;

  files: any[];

  constructor(data: SubmissionJobConstructorParams) {
    super(data);
    this.id = data.id;
    this.sheetController = data.sheetController;
    this.checklists = data.checklists;
    this.checklist = data.checklist;
    this.classrooms = data.classrooms;
    this.classroom = data.classroom;
    this.students = data.students;
    this.student = data.student;
    this.assignments = data.assignments;
    this.assignment = data.assignment;
    this.uploadMethod = data.uploadMethod;
    this.accessToken = data.accessToken;
    this.documentSubmissionType = data.documentSubmissionType;
    this.submittedDocumentState = data.submittedDocumentState;
    this.attachChecklist = data.attachChecklist;

    this.files = data.files;
    this.filesBeingUploaded = ref([]);
  }

  hasMultipleImages = () => {
    return (
      this.files.filter((file) => {
        return file.type.startsWith("image/");
      }).length > 1
    );
  };

  uploadFiles = async () => {
    let checklists: Checklists = this.checklists ?? [];
    let checklist: Checklist | undefined = this.checklist;

    const documentParams = useCreateDocumentParams({
      classroom: this.classroom,
      assignment: this.assignment,
      student: this.student,
      checklist: checklist,
      checklists: checklists,
      documentSubmissionType: this.documentSubmissionType,
      attachChecklist: this.attachChecklist,
    });

    let combineImages = true;

    const wordLikeFiles = this.files.filter((file) => {
      return [
        "application/vnd.google-apps.document",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(file.type);
    });

    const googleSheetFiles = this.files.filter((file) => {
      return ["application/vnd.google-apps.spreadsheet"].includes(file.type);
    });

    const otherFiles = this.files.filter((file) => {
      let excludedFileTypes = [
        "application/vnd.google-apps.document",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.google-apps.spreadsheet",
      ];

      if (combineImages && file.type.startsWith("image/") == true) {
        return false;
      }

      return excludedFileTypes.includes(file.type) != true;
    });

    let imageFiles = this.files
      .filter((file) => {
        return file.type.startsWith("image/");
      })
      .sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

    if (combineImages && imageFiles.length > 0) {
      let imageMediaObjects: MediaObject[] = [];

      const documentId = await createDocument(
        imageFiles[0].name,
        documentParams
      );

      this.filesBeingUploaded.value.push(documentId);

      for (const imageFile of imageFiles) {
        const fileId = imageFile.fileId;
        documentParams.googleDriveFileId = fileId;

        // These files are easy, I just upload hte documetn to firebase storage and create a revision.
        let file: File | CustomFile | undefined = imageFile;

        if (this.uploadMethod == "google") {
          if (this.accessToken == undefined) {
            continue;
          }

          const response = await fetch(
            `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
            {
              headers: new Headers({
                Authorization: "Bearer " + this.accessToken,
              }),
            }
          );

          if (!response.ok) {
            throw new Error(
              `Server responded with ${response.status}: ${response.statusText}`
            );
          }

          const blob = await response.blob();

          const customFile = {
            data: blob,
            name: imageFile.name,
            size: blob.size,
            type: imageFile.type,
          } as CustomFile;

          file = customFile;
        }

        const mediaObject = await useUploadSubmissionToFirestore({
          file,
          documentId,
        });

        if (mediaObject == undefined) {
          continue;
        }

        imageMediaObjects.push(mediaObject);
      }

      await useCreateMediaRevision({
        documentId,
        mediaObjects: imageMediaObjects,
        submittedDocumentState: this.submittedDocumentState,
      });

      this.filesBeingUploaded.value = [
        ...this.filesBeingUploaded.value.filter(
          (id: string) => id !== documentId
        ),
      ];
    }

    for (const googleSheetFile of googleSheetFiles) {
      const fileId = googleSheetFile.fileId;
      documentParams.googleDriveFileId = fileId;

      const documentId = await createDocument(
        googleSheetFile.name,
        documentParams
      );

      this.filesBeingUploaded.value.push(documentId);

      // Export the google drive file as an excel file
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`,
        {
          headers: new Headers({
            Authorization: "Bearer " + this.accessToken,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Server responded with ${response.status}: ${response.statusText}`
        );
      }

      const blob = await response.blob();

      const customFile = {
        data: blob,
        name: `${googleSheetFile.name}.xlsx`,
        size: blob.size,
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      } as CustomFile;
      const mediaObject = await useUploadSubmissionToFirestore({
        file: customFile,
        documentId,
      });

      if (mediaObject == undefined) {
        continue;
      }

      await useCreateMediaRevision({
        documentId,
        mediaObject,
        submittedDocumentState: this.submittedDocumentState,
      });

      this.filesBeingUploaded.value = [
        ...this.filesBeingUploaded.value.filter(
          (id: string) => id !== documentId
        ),
      ];
    }

    for (const otherFile of otherFiles) {
      const fileId = otherFile.fileId;
      documentParams.googleDriveFileId = fileId;

      const documentId = await createDocument(otherFile.name, documentParams);
      this.filesBeingUploaded.value.push(documentId);

      // These files are easy, I just upload hte documetn to firebase storage and create a revision.
      let file: File | CustomFile | undefined = otherFile;

      if (this.uploadMethod == "google") {
        if (this.accessToken == undefined) {
          continue;
        }

        const response = await fetch(
          `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
          {
            headers: new Headers({
              Authorization: "Bearer " + this.accessToken,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(
            `Server responded with ${response.status}: ${response.statusText}`
          );
        }

        const blob = await response.blob();

        const customFile = {
          data: blob,
          name: otherFile.name,
          size: blob.size,
          type: otherFile.type,
        } as CustomFile;

        file = customFile;
      }

      const mediaObject = await useUploadSubmissionToFirestore({
        file,
        documentId,
      });

      if (mediaObject == undefined) {
        continue;
      }

      await useCreateMediaRevision({
        documentId,
        mediaObject,
        submittedDocumentState: this.submittedDocumentState,
      });

      this.filesBeingUploaded.value = [
        ...this.filesBeingUploaded.value.filter(
          (id: string) => id !== documentId
        ),
      ];
    }

    for (const wordLikeFile of wordLikeFiles) {
      documentParams.googleDriveFileId = wordLikeFile.fileId;

      const documentId = await createDocument(
        wordLikeFile.name,
        documentParams
      );

      this.filesBeingUploaded.value.push(documentId);

      const isLargeBulkUpload = wordLikeFiles.length > 5;
      const isLargeFile = wordLikeFile.size > 4.25 * 1024 * 1024;

      if (this.uploadMethod != "google" && (isLargeBulkUpload || isLargeFile)) {
        // I'm going to upload the file to firebase storage:
        useUploadSubmissionToFirestore({
          file: wordLikeFile,
          documentId,
        }).then(async (mediaObject) => {
          if (mediaObject == undefined) {
            this.filesBeingUploaded.value = [
              ...this.filesBeingUploaded.value.filter(
                (id: string) => id !== documentId
              ),
            ];
            return;
          }

          // then here I'm going to create a task.
          await useCreateImportBulkSubmissionTask({
            documentId,
            mediaObject,
          });

          this.filesBeingUploaded.value = [
            ...this.filesBeingUploaded.value.filter(
              (id: string) => id !== documentId
            ),
          ];
        });

        continue;
      }

      if (this.uploadMethod == "google") {
        $fetch(`/api/documents/${documentId}/parse/google`, {
          method: "POST",
          headers: await useApiHeaders(),
          body: {
            fileId: wordLikeFile.fileId,
            fileName: wordLikeFile.name,
            fileType: wordLikeFile.type,
            submittedDocumentState:
              this.submittedDocumentState ?? DocumentState.submitted,
            accessToken: this.accessToken,
          },
        }).then(() => {
          this.filesBeingUploaded.value = [
            ...this.filesBeingUploaded.value.filter(
              (id: string) => id !== documentId
            ),
          ];
        });
        continue;
      }

      const formData = new FormData();

      formData.append("file", wordLikeFile);
      formData.append("fileName", wordLikeFile.name);
      formData.append("fileType", wordLikeFile.type);
      formData.append("method", this.uploadMethod);
      formData.append(
        "submittedDocumentState",
        this.submittedDocumentState ?? DocumentState.submitted
      );

      $fetch(`/api/documents/${documentId}/parse`, {
        method: "POST",
        headers: await useApiHeaders(),
        body: formData,
      }).finally(() => {
        this.filesBeingUploaded.value = [
          ...this.filesBeingUploaded.value.filter(
            (id: string) => id !== documentId
          ),
        ];
      });

      continue;
    }

    this.files = [];
  };
}
